import { rotateAbout, wrapRadians } from '../geometry';
import type { Point, RawFloorPlan, RoomObject, Surface } from '../schemas';

// Grid alignment for a captured floor plan (schema v2: map frame, floor = xy).
//
// RoomPlan hands us walls at whatever angle the room happened to sit relative to
// the scan's starting orientation. For a clean, app-consumable plan we rotate the
// whole room so its walls line up with the X/Y grid (every wall becomes horizontal
// or vertical). We never distort geometry - it's a single rigid rotation of every
// element, so all measurements are preserved.
//
// The rotation is chosen by inlier-length maximization over the wall directions,
// with two rules the caller asked for:
//   1. At least one wall ends up EXACTLY grid-aligned (zero error). That wall is the
//      "anchor". We only ever rotate by the negative of some wall's own deviation,
//      so the anchor snaps perfectly and the rest land as close as the room allows.
//   2. The anchor is the one whose rotation brings the most total wall LENGTH within
//      tolerance of the grid (the "inliers"). Ties go to the longer anchor.
//
// Why inliers and not least squares: real rooms aren't always rectilinear. A wall
// sitting ~45° off the dominant walls (mod 90°) folds to near-maximal deviation under
// any grid that fits the rest, and its squared error is big enough to veto the correct
// rotation - least squares then prefers "everything moderately wrong" over "most of
// the room perfect, one genuinely diagonal wall left diagonal". Counting aligned
// length instead lets the dominant cluster win and leaves true diagonals diagonal.

const HALF_PI = Math.PI / 2;
const QUARTER_PI = Math.PI / 4;

/** Default: ignore walls shorter than 1/4 of the longest wall when fitting the grid. */
const DEFAULT_MIN_WALL_RATIO = 0.25;

/** Default: a wall counts as an inlier when within 10° of a grid axis after rotation. */
const DEFAULT_INLIER_TOLERANCE_DEG = 10;

export type NormalizeOptions = {
  /**
   * Walls shorter than `minWallRatio * longestWall` are excluded from the fit (both
   * as anchor candidates and from the inlier count). Defaults to 0.25.
   */
  minWallRatio?: number;
  /**
   * A wall counts as an inlier for a candidate rotation when its post-rotation
   * deviation from the nearest grid axis is within this many degrees. Defaults to 10.
   */
  inlierToleranceDeg?: number;
};

/** Floor-plane length of a surface's bottom edge (v2 stores endpoints, not length). */
export function surfaceLength(s: Surface): number {
  return Math.hypot(s.end.x - s.start.x, s.end.y - s.start.y);
}

/** Direction angle of a segment in the floor plane, radians CCW from +x. */
function segmentAngle(s: Surface): number {
  return Math.atan2(s.end.y - s.start.y, s.end.x - s.start.x);
}

/**
 * Fold an angle into (−π/4, +π/4]: the signed deviation from the nearest grid axis.
 * On a square grid, horizontal and vertical are equally "aligned", so a wall's error
 * is its distance to whichever axis is closer.
 */
function foldToGrid(angle: number): number {
  let a = angle % HALF_PI; // (−π/2, π/2)
  if (a > QUARTER_PI) a -= HALF_PI;
  else if (a <= -QUARTER_PI) a += HALF_PI;
  return a; // (−π/4, π/4]
}

function rotateSurface(s: Surface, cos: number, sin: number, origin: Point): Surface {
  return {
    ...s,
    start: rotateAbout(s.start, origin, cos, sin),
    end: rotateAbout(s.end, origin, cos, sin),
  };
}

function rotateObject(
  o: RoomObject,
  cos: number,
  sin: number,
  origin: Point,
  theta: number,
): RoomObject {
  // `rotation` is a CCW heading, so rotating the plan CCW by theta adds theta.
  return { ...o, position: rotateAbout(o.position, origin, cos, sin), rotation: o.rotation + theta };
}

/** Centroid of all wall endpoints; falls back to every point, then to the origin. */
function planCentroid(plan: RawFloorPlan): Point {
  const pts: Point[] = [];
  plan.walls.forEach(w => pts.push(w.start, w.end));
  if (pts.length === 0) {
    plan.doors.forEach(s => pts.push(s.start, s.end));
    plan.windows.forEach(s => pts.push(s.start, s.end));
    plan.openings.forEach(s => pts.push(s.start, s.end));
    plan.objects.forEach(o => pts.push(o.position));
  }
  if (pts.length === 0) return { x: 0, y: 0 };
  const sum = pts.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / pts.length, y: sum.y / pts.length };
}

/**
 * Pick the grid-alignment rotation (radians).
 *
 * Candidate rotations are `delta_i = −deviation(wall_i)` over the significant walls -
 * each snaps that wall (the "anchor") exactly onto the grid. A cluster of mutually
 * grid-consistent walls can only be perfectly matched at one of these angles, so no
 * angle sweep is needed. We score each candidate by its inlier length - the total
 * length of significant walls within `tolerance` of a grid axis after rotation - and
 * keep the highest. Candidates are evaluated longest-first with a strict comparison,
 * so equal scores (e.g. two walls of the same cluster) anchor the longest wall.
 */
function fitGrid(significant: Surface[], tolerance: number): number {
  if (significant.length === 0) return 0;

  const byLength = [...significant].sort((a, b) => surfaceLength(b) - surfaceLength(a));
  const angles = byLength.map(segmentAngle);
  const lengths = byLength.map(surfaceLength);

  let bestDelta = 0;
  let bestInlierLength = -Infinity;

  for (let i = 0; i < byLength.length; i++) {
    const delta = -foldToGrid(angles[i]);
    let inlierLength = 0;
    for (let j = 0; j < byLength.length; j++) {
      const dev = foldToGrid(angles[j] + delta);
      if (Math.abs(dev) <= tolerance) inlierLength += lengths[j];
    }
    if (inlierLength > bestInlierLength) {
      bestInlierLength = inlierLength;
      bestDelta = delta;
    }
  }
  return bestDelta;
}

/**
 * After grid alignment, add a multiple of 90° so magnetic north points as close to
 * +y ("map up") as possible. Returns the extra rotation to apply.
 *
 * `northAfterGrid` is north's compass bearing (clockwise from +y) after the grid
 * rotation; we pick the quarter-turn that brings it closest to 0. North is a
 * direction, so it rides along with the geometry, and a 90° turn maps the grid onto
 * itself - so the walls stay aligned. Rotating the plan CCW by `theta` DECREASES a
 * clockwise bearing by `theta`, hence the subtraction.
 */
function cardinalSnapForNorth(northAfterGrid: number | undefined): number {
  if (northAfterGrid == null) return 0;
  let bestK = 0;
  let bestAbs = Infinity;
  for (let k = 0; k < 4; k++) {
    const residual = Math.abs(wrapRadians(northAfterGrid - k * HALF_PI));
    if (residual < bestAbs) {
      bestAbs = residual;
      bestK = k;
    }
  }
  return bestK * HALF_PI;
}

/**
 * Rotate a raw RoomPlan floor plan so its walls align to the X/Y grid and magnetic
 * north points roughly up (+y). Pure: returns a new plan, never mutates the input.
 */
export function normalizeFloorPlan(plan: RawFloorPlan, options: NormalizeOptions = {}): RawFloorPlan {
  const minWallRatio = options.minWallRatio ?? DEFAULT_MIN_WALL_RATIO;
  const inlierTolerance = ((options.inlierToleranceDeg ?? DEFAULT_INLIER_TOLERANCE_DEG) * Math.PI) / 180;

  const longest = plan.walls.reduce((m, w) => Math.max(m, surfaceLength(w)), 0);
  const significant = longest > 0 ? plan.walls.filter(w => surfaceLength(w) >= minWallRatio * longest) : [];

  const gridDelta = fitGrid(significant, inlierTolerance);

  // Rotating the plan CCW by `theta` subtracts `theta` from north's clockwise bearing,
  // so we work the angle forward in lockstep. The grid rotation comes first, then the
  // cardinal snap that brings north closest to straight up.
  const rawAngle = plan.magneticNorthAngle;
  const angleAfterGrid = rawAngle == null ? undefined : rawAngle - gridDelta;

  const theta = gridDelta + cardinalSnapForNorth(angleAfterGrid);

  // (Near-)identity rotation → return the input untouched. More than an
  // optimization: rotateAbout's centroid-relative round-trip
  // (origin + (p − origin)) perturbs every coordinate by ~1 ulp even at
  // theta ≈ 0, and polygon-clipping downstream in the converter relies on
  // coincident coordinates being EXACTLY equal - an already-aligned plan must
  // pass through bit-identical. 1e-9 rad tips a 100 m wall by 0.1 µm: far
  // below sensor noise and every downstream snapping tolerance.
  if (Math.abs(theta) < 1e-9) return plan;

  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const origin = planCentroid(plan);

  const walls = plan.walls.map(s => rotateSurface(s, cos, sin, origin));

  return {
    ...plan,
    walls,
    doors: plan.doors.map(s => rotateSurface(s, cos, sin, origin)),
    windows: plan.windows.map(s => rotateSurface(s, cos, sin, origin)),
    openings: plan.openings.map(s => rotateSurface(s, cos, sin, origin)),
    objects: plan.objects.map(o => rotateObject(o, cos, sin, origin, theta)),
    // North's bearing in the aligned frame (residual within ±45° of up). The raw
    // pre-alignment orientation is arbitrary, so we don't retain it.
    magneticNorthAngle: rawAngle == null ? undefined : wrapRadians(rawAngle - theta),
  };
}
